import { z } from 'zod';
import { ProposalTypeSchema } from './treaties.js';

// ---- Track 5: NPC diplomats (personality, memory, optional language-model speech) ----

export const NpcArchetypeSchema = z.enum(['expansionist', 'industrial', 'militarist', 'peaceful']);
export type NpcArchetype = z.infer<typeof NpcArchetypeSchema>;

/** Hard limit on the free text a player may attach to a diplomatic action. */
export const NPC_PLAYER_MESSAGE_MAX = 200;

export const NpcTalkRequestSchema = z.discriminatedUnion('intent', [
  z.object({ intent: z.literal('propose'), type: ProposalTypeSchema, message: z.string().max(NPC_PLAYER_MESSAGE_MAX).optional(), idempotencyKey: z.string().min(1).max(128) }),
  z.object({ intent: z.literal('ask_help'), resource: z.enum(['metal', 'energy']), message: z.string().max(NPC_PLAYER_MESSAGE_MAX).optional(), idempotencyKey: z.string().min(1).max(128) }),
  z.object({ intent: z.literal('threaten'), message: z.string().max(NPC_PLAYER_MESSAGE_MAX).optional(), idempotencyKey: z.string().min(1).max(128) }),
  z.object({ intent: z.literal('answer_proposal'), proposalId: z.string().uuid(), accept: z.boolean(), message: z.string().max(NPC_PLAYER_MESSAGE_MAX).optional(), idempotencyKey: z.string().min(1).max(128) }),
]);
export type NpcTalkRequest = z.infer<typeof NpcTalkRequestSchema>;

/** Where the NPC's words came from: a model, or the deterministic Hungarian templates. */
export const NpcSpeechSourceSchema = z.enum(['llm', 'template']);
export type NpcSpeechSource = z.infer<typeof NpcSpeechSourceSchema>;

/** What the rules engine actually did — never derived from the speech text. */
export const NpcMechanicalResultSchema = z.object({
  kind: z.string(),
  ok: z.boolean(),
  /** Short, rules-engine-written Hungarian summary. */
  summary: z.string(),
  detail: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
});
export type NpcMechanicalResult = z.infer<typeof NpcMechanicalResultSchema>;

export const NpcConversationMessageSchema = z.object({
  id: z.string().uuid(),
  role: z.enum(['player', 'npc', 'system']),
  /** Speech kind: negotiation | threat | betrayal | gratitude | offer | refusal | request */
  kind: z.string(),
  text: z.string(),
  source: z.enum(['player', 'llm', 'template', 'rules']),
  mechanical: NpcMechanicalResultSchema.nullable(),
  createdAt: z.string().datetime(),
});
export type NpcConversationMessage = z.infer<typeof NpcConversationMessageSchema>;

export const NpcConversationSchema = z.object({
  npc: z.object({
    civId: z.string().uuid(),
    civName: z.string(),
    leaderName: z.string(),
    archetype: NpcArchetypeSchema,
    /** Coarse, player-visible attitude band — never the raw numbers. */
    attitude: z.enum(['hostile', 'wary', 'neutral', 'friendly', 'devoted']),
  }),
  messages: z.array(NpcConversationMessageSchema),
  /** True when NPC speech may be written by a language model (the flag is on and the breaker is closed). */
  llmActive: z.boolean(),
  helpAvailableAt: z.string().datetime().nullable(),
});
export type NpcConversation = z.infer<typeof NpcConversationSchema>;

export const NpcTalkResponseSchema = z.object({
  messages: z.array(NpcConversationMessageSchema),
  mechanical: NpcMechanicalResultSchema,
});
export type NpcTalkResponse = z.infer<typeof NpcTalkResponseSchema>;
